import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom'; // ✨ Import useSearchParams
import { useQuery } from '@tanstack/react-query';
import { useSession } from '@supabase/auth-helpers-react';
import { getPublicSetting, searchFriendById, sendFriendRequest, getFriendRequestsFrontend, cancelFriendRequest, acceptFriendRequest, declineFriendRequest, getFriendships, removeFriend } from '../api/supabaseAPI';
import UserProfilePopover from '../components/UserProfilePopover';
import Message from '../components/Message';
import ToastStack from '../components/ToastStack';
import ConfirmDialog from '../components/ConfirmDialog';
import '../styles/Friends.css';
import '../styles/Layout.css';

const R2_PUBLIC_BASE_URL_ENV = (import.meta.env.VITE_R2_PUBLIC_BASE_URL || '').replace(/\/$/, '');

const Friends = () => {
  const { t } = useTranslation();
  const session = useSession();
  const currentUserId = session?.user?.id || '';
  
  // ✨ Refactor activeTab to use URL Search Params
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'friends'; // Default to 'friends'

  // ✨ Persist Tab Selection
  const STORAGE_KEY_FRIENDS = 'last_active_tab_friends';

  useEffect(() => {
    if (!searchParams.get('tab')) {
      const savedTab = localStorage.getItem(STORAGE_KEY_FRIENDS);
      if (savedTab) {
        setSearchParams({ tab: savedTab }, { replace: true });
      }
    }
  }, []);

  useEffect(() => {
    if (searchParams.get('tab')) {
      localStorage.setItem(STORAGE_KEY_FRIENDS, activeTab);
    }
  }, [activeTab, searchParams]);

  const [activeRequestTab, setActiveRequestTab] = useState('pending');
  const [searchInput, setSearchInput] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searchError, setSearchError] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearchAvatarError, setIsSearchAvatarError] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [popoverAnchor, setPopoverAnchor] = useState({ x: 0, y: 0 });
  const [popoverRect, setPopoverRect] = useState({ width: 300, height: 360 });
  const [searchToast, setSearchToast] = useState({ text: '', type: 'error' });
  const [addFriendStatus, setAddFriendStatus] = useState('idle');
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const [pendingCancelId, setPendingCancelId] = useState('');
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false);
  const [pendingRemoveFriend, setPendingRemoveFriend] = useState(null);
  const [viewedFriend, setViewedFriend] = useState(null);

  const { data: friendsData, isLoading: isLoadingFriends } = useQuery({
    queryKey: ['friendships', currentUserId],
    queryFn: () => getFriendships(currentUserId),
    enabled: !!currentUserId,
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
  });

  const friends = friendsData || [];

  const { data: requestsData, isLoading: isLoadingRequests } = useQuery({
    queryKey: ['friend_requests', currentUserId],
    queryFn: () => getFriendRequestsFrontend(currentUserId),
    enabled: !!currentUserId,
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
  });

  const pendingRequests = requestsData?.pending || [];
  const receivedRequests = requestsData?.received || [];

  const { data: r2PublicBaseUrl } = useQuery({
    queryKey: ['public_settings', 'r2_public_base_url'],
    queryFn: () => getPublicSetting('r2_public_base_url'),
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });

  const publicBaseUrl = useMemo(() => {
    const rawBaseUrl = R2_PUBLIC_BASE_URL_ENV || r2PublicBaseUrl || '';
    return rawBaseUrl ? rawBaseUrl.replace(/\/$/, '') : '';
  }, [r2PublicBaseUrl]);

  const isExternalUrl = useCallback((value) => /^https?:\/\//i.test(value || ''), []);
  const searchAvatarUrl = useMemo(() => {
    const avatarPath = searchResult?.avatar;
    if (!avatarPath) return '';
    if (isExternalUrl(avatarPath)) return avatarPath;
    if (!publicBaseUrl) return avatarPath;
    return `${publicBaseUrl}/${avatarPath}`;
  }, [publicBaseUrl, searchResult?.avatar, isExternalUrl]);

  const searchBackgroundUrl = useMemo(() => {
    const backgroundPath = searchResult?.background;
    if (!backgroundPath) return '';
    if (isExternalUrl(backgroundPath)) return backgroundPath;
    if (!publicBaseUrl) return backgroundPath;
    return `${publicBaseUrl}/${backgroundPath}`;
  }, [publicBaseUrl, searchResult?.background, isExternalUrl]);

  const getAvatarUrl = useCallback((user) => {
    const avatarPath = user?.avatar;
    if (!avatarPath) return '';
    if (isExternalUrl(avatarPath)) return avatarPath;
    if (!publicBaseUrl) return avatarPath;
    return `${publicBaseUrl}/${avatarPath}`;
  }, [publicBaseUrl, isExternalUrl]);

  const getBackgroundUrl = useCallback((user) => {
    const backgroundPath = user?.background;
    if (!backgroundPath) return '';
    if (isExternalUrl(backgroundPath)) return backgroundPath;
    if (!publicBaseUrl) return backgroundPath;
    return `${publicBaseUrl}/${backgroundPath}`;
  }, [publicBaseUrl, isExternalUrl]);

  useEffect(() => {
    setIsSearchAvatarError(false);
  }, [searchAvatarUrl, viewedFriend]);

  useEffect(() => {
    if (!searchResult && !viewedFriend) {
      setIsPopoverOpen(false);
    }
    setAddFriendStatus('idle');
  }, [searchResult, viewedFriend]);

  useEffect(() => {
    if (!searchToast.text) return;
    const timer = setTimeout(() => {
      setSearchToast({ text: '', type: searchToast.type });
    }, 3000);
    return () => clearTimeout(timer);
  }, [searchToast]);

  const updatePopoverPosition = useCallback((anchor, rect) => {
    const padding = 12;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const placements = [
      { key: 'bottom-right', fits: anchor.x + rect.width + padding <= viewportWidth && anchor.y + rect.height + padding <= viewportHeight },
      { key: 'bottom-left', fits: anchor.x - rect.width - padding >= 0 && anchor.y + rect.height + padding <= viewportHeight },
      { key: 'top-right', fits: anchor.x + rect.width + padding <= viewportWidth && anchor.y - rect.height - padding >= 0 },
      { key: 'top-left', fits: anchor.x - rect.width - padding >= 0 && anchor.y - rect.height - padding >= 0 },
    ];

    const chosen = placements.find((placement) => placement.fits) || placements[0];
    let left = anchor.x + padding;
    let top = anchor.y + padding;

    if (chosen.key.includes('left')) {
      left = anchor.x - rect.width - padding;
    }
    if (chosen.key.includes('top')) {
      top = anchor.y - rect.height - padding;
    }

    document.documentElement.style.setProperty('--friend-popover-left', `${Math.max(padding, left)}px`);
    document.documentElement.style.setProperty('--friend-popover-top', `${Math.max(padding, top)}px`);
    document.documentElement.style.setProperty('--friend-popover-placement', chosen.key);
  }, []);

  const handlePopoverMeasure = useCallback((rect) => {
    setPopoverRect((prev) => {
      if (prev.width === rect.width && prev.height === rect.height) {
        return prev;
      }
      return { width: rect.width, height: rect.height };
    });
    updatePopoverPosition(popoverAnchor, { width: rect.width, height: rect.height });
  }, [popoverAnchor, updatePopoverPosition]);

  useEffect(() => {
    if (!isPopoverOpen) return;
    updatePopoverPosition(popoverAnchor, popoverRect);
  }, [isPopoverOpen, popoverAnchor, popoverRect, updatePopoverPosition]);

  useEffect(() => {
    if (!isPopoverOpen) return;
    const handleViewportChange = () => {
      updatePopoverPosition(popoverAnchor, popoverRect);
    };
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [isPopoverOpen, popoverAnchor, popoverRect, updatePopoverPosition]);

  const handleSearchSubmit = async (event) => {
    event.preventDefault();
    const trimmed = searchInput.trim();
    if (!trimmed) {
      setSearchToast({
        text: t('friendsPage.searchRequired', 'Please enter a user ID.'),
        type: 'error',
      });
      setSearchResult(null);
      setHasSearched(false);
      return;
    }

    setIsSearching(true);
    setSearchError('');
    setHasSearched(true);

    try {
      const result = await searchFriendById(trimmed);
      setSearchResult(result);
      if (!result) {
        setSearchError('');
      }
    } catch (error) {
      setSearchResult(null);
      setSearchError(error.message || t('friendsPage.searchError', 'Failed to search user.'));
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddFriend = async () => {
    if (!searchResult?.id) return;
    if (searchResult.id === currentUserId) {
      setSearchToast({
        text: t('friendsPage.cannotAddSelf', 'You cannot add yourself.'),
        type: 'error',
      });
      return;
    }
    try {
      setSearchToast({
        text: t('friendsPage.requestSending', 'Sending request...'),
        type: 'info',
      });
      await sendFriendRequest(searchResult.id);
      setSearchToast({
        text: t('friendsPage.requestSent', 'Friend request sent.'),
        type: 'success',
      });
      setAddFriendStatus('sent');
    } catch (error) {
      if (error.code === 'REQUEST_EXISTS') {
        setSearchToast({
          text: t('friendsPage.requestExists', 'Request already exists. Please check Requests.'),
          type: 'error',
        });
        setAddFriendStatus('sent');
        return;
      }
      if (error.code === 'ALREADY_FRIENDS') {
        setSearchToast({
          text: t('friendsPage.alreadyFriends', 'You are already friends.'),
          type: 'error',
        });
        setAddFriendStatus('sent');
        return;
      }
      setSearchToast({
        text: error.message || t('friendsPage.requestFailed', 'Failed to send friend request.'),
        type: 'error',
      });
    }
  };

  const handleCancelRequest = async (requestId) => {
    try {
      setSearchToast({
        text: t('friendsPage.requestCancelling', 'Cancelling request...'),
        type: 'info',
      });
      await cancelFriendRequest(requestId);
      setSearchToast({
        text: t('friendsPage.requestCancelled', 'Request cancelled.'),
        type: 'success',
      });
    } catch (error) {
      setSearchToast({
        text: error.message || t('friendsPage.requestCancelFailed', 'Failed to cancel request.'),
        type: 'error',
      });
    }
  };

  const openCancelConfirm = (requestId) => {
    setPendingCancelId(requestId);
    setIsCancelConfirmOpen(true);
  };

  const closeCancelConfirm = () => {
    setIsCancelConfirmOpen(false);
    setPendingCancelId('');
  };

  const confirmCancelRequest = async () => {
    if (!pendingCancelId) {
      closeCancelConfirm();
      return;
    }
    await handleCancelRequest(pendingCancelId);
    closeCancelConfirm();
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      setSearchToast({
        text: t('friendsPage.accepting', 'Accepting...'),
        type: 'info',
      });
      await acceptFriendRequest(requestId);
      setSearchToast({
        text: t('friendsPage.accepted', 'Friend request accepted!'),
        type: 'success',
      });
    } catch (error) {
      if (error.code === 'ALREADY_FRIENDS') {
        setSearchToast({
          text: t('friendsPage.alreadyFriends', 'You are already friends.'),
          type: 'error',
        });
        return;
      }
      setSearchToast({
        text: error.message || t('friendsPage.acceptFailed', 'Failed to accept request.'),
        type: 'error',
      });
    }
  };

  const handleDeclineRequest = async (requestId) => {
    try {
      setSearchToast({
        text: t('friendsPage.declining', 'Declining...'),
        type: 'info',
      });
      await declineFriendRequest(requestId);
      setSearchToast({
        text: t('friendsPage.declined', 'Friend request declined.'),
        type: 'success',
      });
    } catch (error) {
      setSearchToast({
        text: error.message || t('friendsPage.declineFailed', 'Failed to decline request.'),
        type: 'error',
      });
    }
  };

  const handleRemoveFriend = async (friendshipId) => {
    try {
      setSearchToast({
        text: t('friendsPage.removing', 'Removing friend...'),
        type: 'info',
      });
      await removeFriend(friendshipId);
      setSearchToast({
        text: t('friendsPage.removed', 'Friend removed.'),
        type: 'success',
      });
      // 成功刪除後關閉 popover 和重置 viewedFriend
      setIsPopoverOpen(false);
      setViewedFriend(null);
    } catch (error) {
      setSearchToast({
        text: error.message || t('friendsPage.removeFailed', 'Failed to remove friend.'),
        type: 'error',
      });
    }
  };

  const openRemoveConfirm = (friend) => {
    setPendingRemoveFriend(friend);
    setIsRemoveConfirmOpen(true);
  };

  const closeRemoveConfirm = () => {
    setIsRemoveConfirmOpen(false);
    setPendingRemoveFriend(null);
  };

  const confirmRemoveFriend = async () => {
    if (!pendingRemoveFriend) {
      closeRemoveConfirm();
      return;
    }
    await handleRemoveFriend(pendingRemoveFriend.friendshipId);
    closeRemoveConfirm();
  };

  return (
    <main className="main-content width-friends friends-page">
      <h1 className="page-title">{t('friendsPage.title', 'Friends')}</h1>
      <ToastStack items={[searchToast]} />

      <div className="friends-tabs-wrap">
        <div className="tabs">
          <button
            className={`tab-btn ${activeTab === 'friends' ? 'active' : ''}`}
            type="button"
            onClick={() => setSearchParams({ tab: 'friends' })}
          >
            {t('friendsPage.friends', 'Friends')}
            <span className="count-badge">{friends.length}</span>
          </button>
          <button
            className={`tab-btn ${activeTab === 'requests' ? 'active' : ''}`}
            type="button"
            onClick={() => setSearchParams({ tab: 'requests' })}
          >
            {t('friendsPage.requests', 'Requests')}
            <span className="count-badge">{pendingRequests.length + receivedRequests.length}</span>
          </button>
          <button
            className={`tab-btn ${activeTab === 'search' ? 'active' : ''}`}
            type="button"
            onClick={() => setSearchParams({ tab: 'search' })}
          >
            {t('friendsPage.searchTab', 'Find Friend')}
          </button>
        </div>
        <div className="tabs-subtitle">
          {activeTab === 'friends'
            ? t('friendsPage.subtitle', 'Connect and manage requests')
            : activeTab === 'requests'
              ? t('friendsPage.requestsSubtitle', 'Manage pending and received requests')
              : t('friendsPage.searchSubtitle', 'Search by user ID to connect')}
        </div>
      </div>

      {activeTab === 'friends' && (
        <div className="friends-card">
          <header className="card-header">
            <h2 className="card-title">{t('friendsPage.friends', 'Friends')}</h2>
          </header>
          <div className="friends-list">
            {friends.map((friend) => {
              const friendAvatarUrl = getAvatarUrl(friend);
              return (
                <div className="friend-item" key={friend.id}>
                  <div className="friend-avatar">
                    {friendAvatarUrl ? (
                      <img
                        className="friend-avatar-img"
                        src={friendAvatarUrl}
                        alt={t('friendsPage.avatarAlt', 'User avatar')}
                        onError={(e) => {
                          e.currentTarget.classList.add('is-hidden');
                        }}
                      />
                    ) : (
                      <span className="friend-avatar-initial">
                        {(friend.username || '?')[0]?.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="friend-info">
                    <div className="friend-name">{friend.username || t('friendsPage.unnamed', 'Unnamed')}</div>
                  </div>
                  <div className="friend-actions">
                    <button
                      className="friends-btn ghost"
                      type="button"
                      onClick={(event) => {
                        setPopoverAnchor({ x: event.clientX, y: event.clientY });
                        setViewedFriend(friend);
                        setIsPopoverOpen(true);
                      }}
                    >
                      {t('friendsPage.view', 'View')}
                    </button>
                    <button
                      className="friends-btn ghost danger"
                      type="button"
                      onClick={() => openRemoveConfirm(friend)}
                    >
                      {t('friendsPage.remove', 'Remove')}
                    </button>
                  </div>
                </div>
              );
            })}
            {!isLoadingFriends && friends.length === 0 && (
              <div className="requests-empty">
                {t('friendsPage.noFriends', 'No friends yet.')}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'requests' && (
        <div className="requests-panel">
          <div className="tabs sub-tabs">
            <button
              className={`tab-btn ${activeRequestTab === 'pending' ? 'active' : ''}`}
              type="button"
              onClick={() => setActiveRequestTab('pending')}
            >
              {t('friendsPage.pending', 'Pending Requests')}
              <span className="count-badge subtle">{pendingRequests.length}</span>
            </button>
            <button
              className={`tab-btn ${activeRequestTab === 'received' ? 'active' : ''}`}
              type="button"
              onClick={() => setActiveRequestTab('received')}
            >
              {t('friendsPage.received', 'Received Requests')}
              <span className="count-badge subtle">{receivedRequests.length}</span>
            </button>
          </div>

          <div className="friends-card">
            <header className="card-header">
              <h2 className="card-title">
                {activeRequestTab === 'pending'
                  ? t('friendsPage.pending', 'Pending Requests')
                  : t('friendsPage.received', 'Received Requests')}
              </h2>
            </header>
          <div className="requests-list">
              {(activeRequestTab === 'pending' ? pendingRequests : receivedRequests).map((request) => {
                const user = request.user || {};
                const avatarUrl = getAvatarUrl(user);
                return (
                <div className="request-item" key={request.id}>
                  <div className="request-avatar">
                    {avatarUrl ? (
                      <img
                        className="request-avatar-img"
                        src={avatarUrl}
                        alt={t('friendsPage.avatarAlt', 'User avatar')}
                        onError={(event) => {
                          event.currentTarget.classList.add('is-hidden');
                        }}
                      />
                    ) : (
                      <span className="request-avatar-initial">
                        {(user.username || '?')[0]?.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="request-info">
                    <div className="request-name">{user.username || t('friendsPage.unnamed', 'Unnamed')}</div>
                  </div>
                  {activeRequestTab === 'pending' ? (
                    <button
                      className="friends-btn ghost"
                      type="button"
                      onClick={() => openCancelConfirm(request.id)}
                    >
                      {t('friendsPage.cancel', 'Cancel')}
                    </button>
                  ) : (
                    <div className="request-actions">
                      <button
                        className="friends-btn primary"
                        type="button"
                        onClick={() => handleAcceptRequest(request.id)}
                      >
                        {t('friendsPage.accept', 'Accept')}
                      </button>
                      <button
                        className="friends-btn ghost"
                        type="button"
                        onClick={() => handleDeclineRequest(request.id)}
                      >
                        {t('friendsPage.decline', 'Decline')}
                      </button>
                    </div>
                  )}
                </div>
              );
              })}
              {!isLoadingRequests && (activeRequestTab === 'pending' ? pendingRequests.length === 0 : receivedRequests.length === 0) && (
                <div className="requests-empty">
                  {t('friendsPage.noRequests', 'No requests yet.')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'search' && (
        <div className="friends-card">
          <header className="card-header">
            <h2 className="card-title">{t('friendsPage.searchTitle', 'Find Friend')}</h2>
          </header>
          <div className="friends-search">
            <form className="search-form" onSubmit={handleSearchSubmit}>
              <div className="search-input-wrap">
                <span className="search-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="11" cy="11" r="7" />
                    <path d="M20 20l-3.5-3.5" />
                  </svg>
                </span>
                <input
                  className="search-input"
                  type="text"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder={t('friendsPage.searchPlaceholder', 'Search by user ID')}
                />
              </div>
              <button className="friends-btn search-btn" type="submit" disabled={isSearching}>
                {isSearching ? t('friendsPage.searching', 'Searching...') : t('friendsPage.searchButton', 'Search')}
              </button>
            </form>
            {searchResult && (
              <div className="search-results has-result">
                <div className="friend-item search-result-item">
                  <div className="friend-avatar">
                    {searchAvatarUrl && !isSearchAvatarError ? (
                      <img
                        className="friend-avatar-img is-visible"
                        src={searchAvatarUrl}
                        alt={t('friendsPage.avatarAlt', 'User avatar')}
                        onError={() => setIsSearchAvatarError(true)}
                      />
                    ) : (
                      <span className="friend-avatar-initial">
                        {(searchResult.username || '?')[0].toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="friend-info">
                    <div className="friend-name">{searchResult.username || t('friendsPage.unnamed', 'Unnamed')}</div>
                    <div className="friend-id">{searchResult.id}</div>
                  </div>
                  <button
                    className="friends-btn ghost"
                    type="button"
                    onClick={(event) => {
                      setPopoverAnchor({ x: event.clientX, y: event.clientY });
                      setViewedFriend(null);
                      setIsPopoverOpen(true);
                    }}
                  >
                    {t('friendsPage.view', 'View')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {isPopoverOpen && (searchResult || viewedFriend) && (
        <UserProfilePopover
          userData={{
            username: (viewedFriend || searchResult).username,
            country: (viewedFriend || searchResult).country,
            avatarUrl: viewedFriend ? getAvatarUrl(viewedFriend) : searchAvatarUrl,
            backgroundUrl: viewedFriend ? getBackgroundUrl(viewedFriend) : searchBackgroundUrl,
          }}
          onClose={() => {
            setIsPopoverOpen(false);
            setViewedFriend(null);
          }}
          showEmail={false}
          showActions={false}
          showCountry={true}
          showAddFriend={!viewedFriend}
          onAddFriend={handleAddFriend}
          addFriendStatus={addFriendStatus}
          variant="friends"
          onMeasure={handlePopoverMeasure}
        />
      )}

      <ConfirmDialog
        isOpen={isCancelConfirmOpen}
        title={t('friendsPage.cancelConfirmTitle', 'Cancel friend request?')}
        message={t('friendsPage.cancelConfirmMessage', 'This request will be removed.')}
        onConfirm={confirmCancelRequest}
        onCancel={closeCancelConfirm}
      />

      <ConfirmDialog
        isOpen={isRemoveConfirmOpen}
        title={t('friendsPage.removeConfirmTitle', 'Remove Friend?')}
        message={t('friendsPage.removeConfirmMessage', 'Are you sure you want to remove this friend?')}
        onConfirm={confirmRemoveFriend}
        onCancel={closeRemoveConfirm}
      />
    </main>
  );
};

export default Friends;
